<template>
  <section
    class="rounded-xl border border-gray-200 p-4 text-[14px] leading-[20px]"
    aria-label="Audio match methods"
  >
    <h2 class="font-medium mb-3">Audio match methods</h2>
    <div class="grid gap-3 tablet-h:grid-cols-2">
      <div v-for="method in methods" :key="method.label">
        <h3 class="font-medium">{{ method.label }}</h3>
        <p>
          {{ formatState(method.status?.state ?? 'not_run')
          }}<template v-if="method.status?.outcome">
            · {{ formatState(method.status.outcome) }}</template
          >
        </p>
        <p v-if="method.status?.reason" class="text-gray-500">
          {{ formatState(method.status.reason) }}
        </p>
        <p v-else-if="!method.status" class="text-gray-500">
          Unavailable for this result.
        </p>
      </div>
    </div>
    <p class="mt-3">{{ verdict }}</p>
    <p v-if="bundle && !bundle.searchedScopeComplete" class="text-gray-500">
      The search scope is incomplete.
    </p>
    <p v-if="bundle && !bundle.catalogueComplete" class="text-gray-500">
      The catalogue is incomplete.
    </p>
    <p v-if="bundle?.totalWindows != null" class="text-gray-500">
      Windows: {{ bundle.processedWindows ?? 'unknown' }} processed,
      {{ bundle.unprocessedWindows ?? 'unknown' }} unprocessed,
      {{ bundle.totalWindows }} total.
    </p>
    <div
      v-for="work in acceptedWorks"
      :key="work.fileId"
      class="mt-4 border-t border-gray-200 pt-3"
    >
      <p class="font-medium break-all">Accepted work: {{ work.fileId }}</p>
      <template v-if="work.matchSources.includes('audio_cover')">
        <p>Accepted cover match</p>
        <p v-if="work.coverWitnessesComplete === false" class="text-gray-500">
          Some cover witness details are unavailable.
        </p>
        <p
          v-else-if="work.coverWitnessesComplete == null"
          class="text-gray-500"
        >
          Cover witness detail completeness is unknown.
        </p>
      </template>
      <div
        v-for="(witness, index) in work.coverWitnesses"
        :key="index"
        class="mt-2 rounded-lg bg-gray-200/20 p-3"
      >
        <p>
          {{ witness.admitted ? 'Accepted cover evidence' : 'Cover witness' }} ·
          {{ witness.method }}
        </p>
        <p class="break-all">Source view: {{ witness.viewId }}</p>
        <p>
          Window {{ witness.windowIndex
          }}<template v-if="witness.startMs != null">
            · Start: {{ witness.startMs }} ms</template
          ><template v-if="witness.spanMs != null">
            · Span: {{ witness.spanMs }} ms</template
          >
        </p>
        <details class="mt-2 text-gray-500">
          <summary class="cursor-pointer">Technical evidence</summary>
          <p class="break-all">Registration: {{ witness.registrationId }}</p>
          <p>Native score: {{ witness.score }} · Gate: {{ witness.gate }}</p>
          <p v-if="witness.topTied">This witness has a tied top score.</p>
          <p>Cover scores are native method values, not percentages.</p>
        </details>
      </div>
    </div>
    <p
      v-if="acceptedWorks.some((work) => work.coverWitnesses.length)"
      class="mt-3 text-gray-500"
    >
      The windows identify query evidence. They do not prove an exact reused
      span.
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { AudioBundle } from '@/shared/graphql/router/graphql'

const formatState = (value: string) => value.replace(/_/g, ' ')

const props = defineProps<{ bundle?: AudioBundle | null }>()
const methods = computed(() => [
  { label: 'Regular audio', status: props.bundle?.regularStatus },
  { label: 'Cover audio', status: props.bundle?.coverStatus },
])
const acceptedWorks = computed(
  () => props.bundle?.acceptedWorks.filter((work) => work.accepted) ?? []
)
const verdict = computed(() =>
  acceptedWorks.value.length
    ? 'The audio methods accepted a work.'
    : props.bundle?.noMatch === true &&
        props.bundle.searchedScopeComplete &&
        props.bundle.catalogueComplete &&
        props.bundle.acceptedWorksComplete
      ? 'No audio match in the complete searched scope.'
      : 'The audio methods do not establish a complete no-match result.'
)
</script>
